import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { apiClient } from '@/core/api/client';
import { getSelectionStatus } from '@/features/partner-signup/api/partnerSignupApi';
import { Spinner } from '@/shared/components/Loading/Spinner';
import { useSignupContext } from '@/features/pre-login/hooks/useSignupContext';
import { updateFpAssociation } from '@/features/pre-login/api/screen2Api';
import { claimGuestCarePlan } from '@/features/chatbot/care-navigator/api/claimGuestCarePlanApi';
import { claimAssessment } from '@/features/longevity-assessment/api/careSeekerApi';
import {
  readAssessmentClaimToken,
  clearAssessmentClaimToken,
} from '@/features/longevity-assessment/hooks/useTakeAssessment';
import { trackRumAction } from '@/shared/observability/rum';
import { syncUserClaims, verifySso } from '../api/authApi';

const AUTH_INTENT_KEY = 'bquest_auth_intent';

type AuthIntent = {
  method: 'otp' | 'sso';
  companySlug?: string | null;
  email?: string;
  expectedCompanySlug?: string | null;
};

type ResolvedAuthIntent = {
  method: 'otp' | 'sso';
  companySlug: string | null;
  email: string | null;
  expectedCompanySlug: string | null;
};

type CompleteRegistrationRequest = {
  kindeId: string;
  email: string;
  firstName: string;
  lastName?: string;
  signupAttribution?: {
    anonSessionId?: string;
    signupTrigger?: string;
    signupTriggerEntityId?: string;
    signupReferralCode?: string;
  };
  inviteId?: string;
};

type UserProfile = {
  id: string;
  email: string;
  firstName: string;
  lastName?: string;
  role: string;
  onboardingCompleted?: boolean;
};

type JwtTokenPayload = {
  given_name?: string;
  family_name?: string;
  name?: string;
};

type AuthCallbackUser = {
  id?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  user_id?: string;
};

const getTargetRouteForRole = (
  role: string,
  onboardingCompleted?: boolean,
): string => {
  switch (role) {
    case 'CARE_SEEKER':
      return onboardingCompleted === false ? '/onboarding' : '/dashboard/care-seeker';
    case 'SERVICE_PROVIDER':
      return '/dashboard/service-provider';
    case 'FINANCIAL_ADVISOR':
      return '/dashboard/financial-advisor';
    case 'ADMIN':
      return '/dashboard/admin';
    default:
      return '/dashboard';
  }
};

const readAuthIntent = (): AuthIntent | null => {
  const rawIntent = sessionStorage.getItem(AUTH_INTENT_KEY);
  if (!rawIntent) {
    return null;
  }

  try {
    const parsedIntent = JSON.parse(rawIntent) as AuthIntent;
    return parsedIntent.method === 'otp' || parsedIntent.method === 'sso'
      ? parsedIntent
      : null;
  } catch {
    return null;
  }
};

const resolveAuthIntent = (
  intent: AuthIntent | null,
  fallbackEmail?: string,
): ResolvedAuthIntent => {
  if (!intent || (intent.method !== 'otp' && intent.method !== 'sso')) {
    return {
      method: 'otp',
      companySlug: null,
      email: fallbackEmail ?? null,
      expectedCompanySlug: null,
    };
  }

  return {
    method: intent.method,
    companySlug: intent.companySlug ?? null,
    email: intent.email ?? fallbackEmail ?? null,
    expectedCompanySlug: intent.expectedCompanySlug ?? null,
  };
};

const decodeJwtToken = (token: string): JwtTokenPayload | null => {
  try {
    const encodedPayload = token.split('.')[1];
    if (!encodedPayload) {
      return null;
    }

    const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as JwtTokenPayload;
  } catch {
    return null;
  }
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'data' in error.response &&
    typeof error.response.data === 'object' &&
    error.response.data !== null &&
    'message' in error.response.data &&
    typeof error.response.data.message === 'string'
  ) {
    return error.response.data.message;
  }

  return fallback;
};

/**
 * Safe-redirect guard for the post-signup deep link. The `deepLinkPath` value
 * is sourced from localStorage (attacker-controllable: XSS or social engineering)
 * and assigned to `window.location.href`, so it must be a same-origin relative
 * path. Rejects:
 *  - Absolute URLs (`https://evil.example/...`)
 *  - Protocol-relative URLs (`//evil.example/...`)
 *  - Anything not starting with a single `/`
 *  - Non-strings (corrupt localStorage payload)
 */
const isSafeRelativePath = (path: unknown): path is string => {
  if (typeof path !== 'string') return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  try {
    const url = new URL(path, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
};

export function AuthCallback() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, getToken, getIdToken } = useKindeAuth();
  const handledRef = useRef(false);
  const [loadingMessage, setLoadingMessage] = useState('Authenticating...');
  const { read: readSignupContext, clear: clearSignupContext } = useSignupContext();

  const kindeUser = user as unknown as AuthCallbackUser | null;

  const refreshTokenSafely = async () => {
    try {
      await getToken();
      await new Promise((resolve) => setTimeout(resolve, 300));
    } catch {
      // Token refresh is best-effort only.
    }
  };

  const maybeRedirectToAdvisorSelection = async () => {
    try {
      setLoadingMessage('Checking your account status...');
      const selectionStatus = await getSelectionStatus();
      if (selectionStatus.requiresAdvisorSelection) {
        window.location.href = '/select-advisor';
        return true;
      }
    } catch {
      // Do not block login on this non-critical check.
    }

    return false;
  };

  const redirectToLogin = (intent: ResolvedAuthIntent, errorMessage: string) => {
    const basePath = intent.expectedCompanySlug ? `/login/${intent.expectedCompanySlug}` : '/login';
    const params = new URLSearchParams();

    if (intent.email) {
      params.set('email', intent.email);
    }
    params.set('error', errorMessage);

    navigate(`${basePath}?${params.toString()}`, { replace: true });
  };

  const completeOtpRegistration = async () => {
    if (!kindeUser?.id || !kindeUser.email) {
      throw new Error('Authentication failed');
    }

    setLoadingMessage('Completing authentication...');
    const idToken = await getIdToken();
    const decodedToken = idToken ? decodeJwtToken(idToken) : null;

    // Pre-login signup context (Item 3): when the visitor came through the gated
    // AccountCreationModal we have name + attribution in localStorage. Prefer those
    // over Kinde-derived names so we don't lose the user-typed casing.
    const signupContext = readSignupContext();

    const firstName =
      signupContext?.firstName ??
      decodedToken?.given_name ?? kindeUser.given_name ?? kindeUser.name?.split(' ')[0] ?? 'User';
    const lastName =
      signupContext?.lastName ??
      decodedToken?.family_name ??
      kindeUser.family_name ??
      (kindeUser.name?.split(' ').slice(1).join(' ') || undefined);

    const signupAttribution = signupContext
      ? {
          anonSessionId: signupContext.anonSessionId,
          signupTrigger: signupContext.trigger,
          signupTriggerEntityId: signupContext.triggerEntityId,
          signupReferralCode: signupContext.signupReferralCode,
        }
      : undefined;

    const response = await apiClient.post<UserProfile>('/auth/complete-registration', {
      kindeId: kindeUser.id,
      email: kindeUser.email,
      firstName,
      lastName,
      ...(signupAttribution ? { signupAttribution } : {}),
      ...(signupContext?.inviteId ? { inviteId: signupContext.inviteId } : {}),
    } as CompleteRegistrationRequest);

    // Fire unauth.signup_completed (spec linha 171) — only when the user came through
    // the AccountCreationModal flow (signupContext present). Existing-user logins
    // hitting this same callback are NOT signups and should not fire this event.
    if (signupContext) {
      trackRumAction('unauth.signup_completed', {
        userId: response.data.id,
        trigger: signupContext.trigger,
        anonSessionId: signupContext.anonSessionId,
        ...(signupContext.signupReferralCode && {
          signupReferralCode: signupContext.signupReferralCode,
        }),
      });
    }

    await refreshTokenSafely();

    // Pre-login modal Screen 2 follow-up (spec Item 3): if Screen 2 was answered
    // before Kinde, apply the FP association now that the user is authenticated.
    // Best-effort — failures don't block the post-signup flow.
    const screen2 = signupContext?.screen2;
    if (screen2 && screen2.fpAnswer === 'yes') {
      try {
        await updateFpAssociation({
          partnerCompanyId: screen2.partnerCompanyId,
          advisorId: screen2.advisorId,
          unmatchedAdvisorName: screen2.unmatchedAdvisorName,
        });
      } catch {
        // Swallow — signup succeeded; FP association can be set later.
      }
    }

    // Capture the deep-link target + guest navigator session, then clear the signup
    // context BEFORE any early return below. Otherwise the advisor-selection redirect
    // path would leave bquest_signup_context in localStorage and mis-attribute a
    // future auth callback (e.g. unauth.signup_completed firing on a normal login).
    // SECURITY: deepLinkPath comes from localStorage, which is attacker-controllable
    // (XSS or social engineering). Validate it's a same-origin relative path before
    // assigning to window.location.href to close an open-redirect vector.
    const deepLinkPath = signupContext?.deepLinkPath;
    const navigatorSessionId = signupContext?.navigatorSessionId;
    clearSignupContext();

    // Claim a care plan the user built while anonymous so it persists to their new
    // account, then drop them back on the plan. Best-effort: the token can be briefly
    // stale right after registration, and a failed claim must never block signup.
    let navigatorResultsRedirect: string | null = null;
    if (navigatorSessionId) {
      try {
        await claimGuestCarePlan(navigatorSessionId);
        // Re-seed so /care-navigator/results resolves the session after the Kinde
        // round-trip even if sessionStorage was lost.
        sessionStorage.setItem('bquest_nav_session_id', navigatorSessionId);
        navigatorResultsRedirect = '/care-navigator/results';
      } catch {
        // Swallow — the account exists; the user can rebuild/find the plan later.
      }
    }

    // Claim an assessment the user completed while anonymous so it persists to their
    // new account, then drop them on their results (shared with the existing-user
    // sign-in branch in handleOtpCallback).
    const longevityResultsRedirect = await claimStashedAssessment();

    if (await maybeRedirectToAdvisorSelection()) {
      return;
    }

    // A just-claimed care plan is the user's most recent intent — return them to it
    // ahead of the generic deep link or role dashboard.
    if (navigatorResultsRedirect) {
      window.location.href = navigatorResultsRedirect;
      return;
    }

    // Same for a just-claimed longevity assessment — drop the taker on their results
    // instead of the generic care-seeker welcome landing.
    if (longevityResultsRedirect) {
      window.location.href = longevityResultsRedirect;
      return;
    }

    // Prefer the modal's deep link target (provider profile / resource detail) over
    // the role-based dashboard when present — fulfills the spec's "drop user on the
    // page they were trying to access" promise (Item 4 fallback path).
    if (deepLinkPath && isSafeRelativePath(deepLinkPath)) {
      window.location.href = deepLinkPath;
      return;
    }

    window.location.href = getTargetRouteForRole(
      response.data.role,
      response.data.onboardingCompleted,
    );
  };

  /**
   * Claim an assessment the user completed while anonymous so it persists to
   * their account, and return the results path to land them on. Shared by the
   * registration path and the existing-user sign-in path — the take flow's
   * "Sign in & view results" CTA stashes the token before either. Best-effort:
   * a failed claim must never block auth (CareSeekerLongevityCard claims it on
   * mount as a fallback).
   */
  const claimStashedAssessment = async (): Promise<string | null> => {
    const assessmentClaimToken = readAssessmentClaimToken();
    if (!assessmentClaimToken) return null;
    try {
      const { assessmentId } = await claimAssessment(assessmentClaimToken);
      clearAssessmentClaimToken();
      return assessmentId ? `/longevity-assessment/results/${assessmentId}` : null;
    } catch {
      // Swallow — the dashboard card claims it on mount later.
      return null;
    }
  };

  const handleOtpCallback = async () => {
    if (!kindeUser?.id) {
      throw new Error('Authentication failed');
    }

    if (kindeUser.user_id) {
      setLoadingMessage('Syncing your account...');
      const syncedUser = await syncUserClaims({ kindeId: kindeUser.id });
      await refreshTokenSafely();

      const longevityResultsRedirect = await claimStashedAssessment();

      if (await maybeRedirectToAdvisorSelection()) {
        return;
      }

      // A just-claimed assessment is the sign-in's whole intent ("Sign in &
      // view results") — land on the report, not the role dashboard.
      if (longevityResultsRedirect) {
        window.location.href = longevityResultsRedirect;
        return;
      }

      window.location.href = getTargetRouteForRole(
        syncedUser.role,
        syncedUser.onboardingCompleted,
      );
      return;
    }

    await completeOtpRegistration();
  };

  const handleSsoCallback = async (intent: ResolvedAuthIntent) => {
    const accessToken = await getToken();
    const idToken = await getIdToken();

    if (!accessToken || !idToken) {
      redirectToLogin(intent, 'missing_tokens');
      return;
    }

    setLoadingMessage('Verifying SSO authentication...');
    const ssoResult = await verifySso({
      accessToken,
      idToken,
      ...(intent.expectedCompanySlug ? { expectedCompanySlug: intent.expectedCompanySlug } : {}),
    });

    if (ssoResult.claimsSynced) {
      await refreshTokenSafely();
    }

    window.location.href = getTargetRouteForRole(ssoResult.user.role);
  };

  useEffect(() => {
    if (handledRef.current || isLoading) {
      return;
    }

    if (!isAuthenticated || !kindeUser) {
      navigate('/login?error=authentication_failed', { replace: true });
      return;
    }

    handledRef.current = true;
    const authIntent = resolveAuthIntent(readAuthIntent(), kindeUser.email);
    sessionStorage.removeItem(AUTH_INTENT_KEY);

    void (async () => {
      try {
        if (authIntent.method === 'sso') {
          await handleSsoCallback(authIntent);
          return;
        }

        await handleOtpCallback();
      } catch (error) {
        const fallbackMessage =
          authIntent.method === 'sso'
            ? 'SSO authentication failed'
            : 'registration_failed';
        redirectToLogin(authIntent, getErrorMessage(error, fallbackMessage));
      }
    })();
  }, [isAuthenticated, isLoading, kindeUser, navigate]);

  return (
    <div className="flex h-screen items-center justify-center bg-blue-50">
      <div className="text-center p-8 bg-white rounded-lg shadow-lg">
        <Spinner className="mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{loadingMessage}</h2>
        <p className="text-gray-600 mb-4">Please wait a moment</p>
      </div>
    </div>
  );
}

